import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getOwaCalendarStrings } from 'owa-strings/owa-calendar-strings';
import isItemMultiDay from '../store/calculation/isItemMultiDay';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { format } from 'owa-strings';
import {
    addMilliseconds,
    OwaDate,
    formatUserTime,
    formatWeekDayMonthDayYear,
    owaDate,
    isSameDay,
    differenceInMilliseconds,
} from 'owa-datetime';

export function getCalendarSurfaceItemAriaLabel(item: CalendarEvent, timeZoneId?: string) {
    let stringBuilder = [];

    stringBuilder.push(WhenAndTitle(item, timeZoneId));

    if (item.Location) {
        stringBuilder.push(' ');
        stringBuilder.push(
            format(getOwaCalendarStrings().eventLocationAriaText, item.Location.DisplayName)
        );
    }

    if (item.Organizer && item.IsMeeting) {
        stringBuilder.push(' ');
        stringBuilder.push(
            format(getOwaCalendarStrings().eventOrganizerAriaText, item.Organizer.Mailbox.Name)
        );
    }

    return stringBuilder.join('');
}

export function getCalendarSurfaceItemTitle(item: CalendarEvent, timeZoneId?: string) {
    let isMultidayEvent = isItemMultiDay(item);
    let when: string = GetWhenStringForScreenReader(
        item.Start,
        item.End,
        timeZoneId,
        item.IsAllDayEvent,
        isMultidayEvent,
        false // don't include the date
    );

    return (item.Subject ? item.Subject + '\n' : '') + when;
}

function WhenAndTitle(item: CalendarEvent, timeZoneId: string): string {
    // in jsMVVM, which we're porting here, "multi-day" event definition is when length > 24 hours or is marked as an all-day event
    let isMultidayEvent = isItemMultiDay(item);
    let when: string = GetWhenStringForScreenReader(
        item.Start,
        item.End,
        timeZoneId,
        item.IsAllDayEvent,
        isMultidayEvent,
        true // include both date and time
    );

    let stringFormat: string = getOwaCalendarStrings().eventDescriptionAriaText;
    if (
        item.IsAllDayEvent &&
        isMultidayEvent &&
        differenceInMilliseconds(item.End, item.Start) == TimeConstants.MillisecondsInOneDay
    ) {
        stringFormat = getOwaCalendarStrings().singleDayEventDescriptionAriaText;
    } else if (item.IsAllDayEvent && isMultidayEvent) {
        stringFormat = getOwaCalendarStrings().allDayEventDescriptionAriaText;
    }

    return format(stringFormat, when, item.Subject);
}

/**
 * This function builds a string from the item's info.
 * TODO: VSO 19024 currently this does not take locales into account, as owa-datetime implemention isn't finished. Will need to update
 * to use locales
 * @param start
 * @param end
 * @param isAllDayEvent
 * @param isMultiDayEvent
 * @param includeDate whether to include the date in the generated string. False includes only times
 */
function GetWhenStringForScreenReader(
    start: OwaDate,
    end: OwaDate,
    timeZoneId: string,
    isAllDayEvent: boolean,
    isMultiDayEvent: boolean,
    includeDate: boolean
): string {
    if (timeZoneId) {
        start = owaDate(timeZoneId, start);
        end = owaDate(timeZoneId, end);
    }

    if (isSameDay(start, end)) {
        return format(
            getOwaCalendarStrings().whenStartEndsSameDayLongAriaText,
            includeDate ? formatWeekDayMonthDayYear(start) : '',
            formatUserTime(start),
            formatUserTime(end)
        ).replace('  ', ' '); // strip out any unnecessary blank space
    }

    if (
        isAllDayEvent &&
        isMultiDayEvent &&
        differenceInMilliseconds(end, start) == TimeConstants.MillisecondsInOneDay
    ) {
        return formatWeekDayMonthDayYear(start);
    }

    if (isAllDayEvent && isMultiDayEvent) {
        // An all-day-event from Mon to Tue has start date Mon 12:00AM and end date
        // as Wed 12:00AM. The add milliseconds -1 makes sure we announce Tue as end day (otherwise we
        // will announce Wed).
        let endDate: string = formatWeekDayMonthDayYear(addMilliseconds(end, -1));
        return format(
            getOwaCalendarStrings().whenStartEndsDifferentDayDateOnlyLongAriaText,
            formatWeekDayMonthDayYear(start),
            endDate
        );
    }

    return format(
        getOwaCalendarStrings().whenStartEndsDifferentDayLongAriaText,
        formatWeekDayMonthDayYear(start),
        formatUserTime(start),
        formatWeekDayMonthDayYear(end),
        formatUserTime(end)
    );
}
